<template>
  <section id="entry-index" class="layout-section -bg-shade -search-top">
    <p v-if="$wp.secondaryDataLoaded && !entriesPage.length" class="layout-section__heading">Aucun résultat</p>

    <article v-else class="card-article">
      <MultiCard
        v-for="(entry, index) in entriesPage"
        :key="getEntryKey(entry)"
        v-scroll:reveal="{ delay: (index % 3) * 75, offset: 0 }"
        :entry="entry"
      />
    </article>

    <PagePickerQuery :total-pages="totalPages" />
  </section>
</template>

<script>
import { safeArray, normalizeForSearch, normalizeJsonForSearch } from '@/helpers/search-helpers'

const perPage = 18

export default {
  name: 'SearchIndex',
  computed: {
    searchTerm() {
      return normalizeForSearch(this.$route.query.search || '')
    },
    currentKeywords() {
      if (!this.$route.query.keywords) return []
      return Array.isArray(this.$route.query.keywords) ? this.$route.query.keywords : [this.$route.query.keywords]
    },
    currentPage() {
      return this.$route.query.page ? Math.max(parseInt(this.$route.query.page) - 1, 0) : 0
    },
    currentContentType() {
      return this.$route.query.contentType || null
    },
    currentPublicationType() {
      return this.$route.query.publicationType || null
    },
    currentEventType() {
      return this.$route.query.eventType || null
    },
    filteredPages() {
      if ((this.currentContentType && this.currentContentType !== 'page') || this.currentKeywords.length) return []

      return Object.values(this.$wp.pages).map(page => ({
        ...page,
        isPage: true,
        type: 'page'
      }))
    },
    filteredProjects() {
      if (this.currentContentType && this.currentContentType !== 'project') return []

      const projects = Object.values(this.$wp.projects).map(project => ({
        ...project,
        isProject: true,
        type: 'project'
      }))

      return this.filterKeywordMatches(projects)
    },
    filteredPublications() {
      const isFiltered =
        this.currentContentType && this.currentContentType !== 'publication' && this.currentContentType !== 'monitoring'

      if (isFiltered) return []

      const publications = [...this.$wp.restPublications, ...this.$wp.monitoringPublications].map(pub => ({
        ...pub,
        isPublication: true
      }))

      const filteredProjects = this.currentPublicationType
        ? publications.filter(pub => pub.type === this.currentPublicationType)
        : publications

      return this.filterKeywordMatches(filteredProjects)
    },
    filteredNews() {
      if (this.currentContentType && this.currentContentType !== 'news') return []

      return this.filterKeywordMatches(this.$wp.newsPublications.map(pub => ({ ...pub, isNews: true })))
    },
    filteredEvents() {
      if (this.currentContentType && this.currentContentType !== 'event') return []

      const events = this.$wp.eventPublications.map(pub => ({ ...pub, isEvent: true, type: 'event' }))
      const filteredEvents = this.currentEventType
        ? events.filter(pub => pub.eventType === this.currentEventType)
        : events

      return this.filterKeywordMatches(filteredEvents)
    },
    matchedPages() {
      if (!this.searchTerm) return this.filteredPages

      return this.filteredPages.filter(page => {
        const searchableContent = normalizeJsonForSearch(JSON.stringify(Object.values(page)))
        return searchableContent.includes(this.searchTerm)
      })
    },
    matchedProjects() {
      if (!this.searchTerm) return this.filteredProjects

      return this.filteredProjects.filter(({ content, title, intro = {}, keywords = [] }) => {
        const searchableContent = [
          normalizeForSearch(content),
          normalizeForSearch(title),
          normalizeForSearch(`${intro.title} ${intro.text}`),
          normalizeForSearch(
            safeArray(keywords)
              .map(id => this.$wp.keywordsById[id] && this.$wp.keywordsById[id].name)
              .join(' ')
          )
        ]

        return searchableContent.join(' ').includes(this.searchTerm)
      })
    },
    matchedPublications() {
      if (!this.searchTerm) return this.filteredPublications

      return this.filteredPublications.filter(({ content, title, keywords = [], authors = [] }) => {
        const searchableContent = [
          normalizeForSearch(content),
          normalizeForSearch(title),
          normalizeForSearch(
            safeArray(keywords)
              .map(id => this.$wp.keywordsById[id] && this.$wp.keywordsById[id].name)
              .join(' ')
          ),
          normalizeForSearch(
            safeArray(authors)
              .map(id => this.$wp.authorsById[id] && this.$wp.authorsById[id].name)
              .join(' ')
          )
        ]

        return searchableContent.join(' ').includes(this.searchTerm)
      })
    },
    matchedNews() {
      if (!this.searchTerm) return this.filteredNews

      return this.filteredNews.filter(({ content, title, keywords = [], authors = [] }) => {
        const searchableContent = [
          normalizeForSearch(content),
          normalizeForSearch(title),
          normalizeForSearch(
            safeArray(keywords)
              .map(id => this.$wp.keywordsById[id] && this.$wp.keywordsById[id].name)
              .join(' ')
          ),
          normalizeForSearch(
            safeArray(authors)
              .map(id => this.$wp.authorsById[id] && this.$wp.authorsById[id].name)
              .join(' ')
          )
        ]

        return searchableContent.join(' ').includes(this.searchTerm)
      })
    },
    matchedEvents() {
      if (!this.searchTerm) return this.filteredEvents

      return this.filteredEvents.filter(({ content, title, keywords = [] }) => {
        const searchableContent = [
          normalizeForSearch(content),
          normalizeForSearch(title),
          normalizeForSearch(
            safeArray(keywords)
              .map(id => this.$wp.keywordsById[id] && this.$wp.keywordsById[id].name)
              .join(' ')
          )
        ]

        return searchableContent.join(' ').includes(this.searchTerm)
      })
    },
    allEntries() {
      return [
        ...this.matchedPages,
        ...this.matchedProjects,
        ...this.matchedPublications,
        ...this.matchedNews,
        ...this.matchedEvents
      ]
    },
    entriesPage() {
      return this.allEntries.slice(this.currentPage * perPage, (this.currentPage + 1) * perPage)
    },
    totalPages() {
      return Math.ceil(this.allEntries.length / perPage)
    }
  },
  methods: {
    getEntryKey(entry) {
      const key = entry.id || `${entry.slug}-${entry.type}`
      return `${key}-${JSON.stringify(this.$route.query)}`
    },
    filterKeywordMatches(entries) {
      if (!this.currentKeywords.length) return entries

      return entries.filter(entry => {
        if (!entry || !entry.keywords) return false

        return safeArray(entry.keywords)
          .map(id => this.$wp.keywordsById[id] && this.$wp.keywordsById[id].slug)
          .filter(Boolean)
          .some(slug => this.currentKeywords.includes(slug))
      })
    }
  }
}
</script>
